import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { useAppTheme } from '../../contexts/ThemeContext';

const ProfileSettings = ({ showNotification }) => {
  const { palette, Colors } = useAppTheme();
  const [isEditing, setIsEditing] = useState(false);

  const [userSettings, setUserSettings] = useState({
    firstName: 'Admin',
    lastName: 'User',
    email: 'admin@abcpropertymanagement.com',
    phone: '(555) 987-6543',
    role: 'Administrator'
  });

  const [originalSettings, setOriginalSettings] = useState({ ...userSettings });

  const handleUserSettingsChange = (field) => (event) => {
    setUserSettings({ ...userSettings, [field]: event.target.value });
  };

  const handleEdit = () => {
    setOriginalSettings({ ...userSettings });
    setIsEditing(true);
  };

  const handleSaveSettings = () => {
    setIsEditing(false);
    showNotification('Profile settings saved successfully');
  };

  const handleCancel = () => {
    setUserSettings({ ...originalSettings });
    setIsEditing(false);
  };

  const paperSx = {
    backgroundColor: palette.background.tertiary,
    border: `1px solid ${palette.border}`,
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: Colors.primary.main },
      backgroundColor: palette.background.secondary,
    },
    '& .MuiInputLabel-root': { color: palette.text.secondary },
    '& .MuiInputBase-input': { color: palette.text.primary },
    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: palette.text.secondary },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: palette.text.primary }}>User Profile</Typography>
        {!isEditing && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderColor: Colors.primary.main, color: Colors.primary.main }}
          >
            Edit
          </Button>
        )}
      </Box>

      <Paper elevation={isEditing ? 2 : 0} sx={{ p: isEditing ? 3 : 0, ...(isEditing ? paperSx : {}) }}>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Avatar sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: Colors.primary.main }}>
              {userSettings.firstName.charAt(0) + userSettings.lastName.charAt(0)}
            </Avatar>
            {isEditing && (
              <Button variant="outlined" sx={{ borderColor: Colors.primary.main, color: Colors.primary.main }}>
                Change Photo
              </Button>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="First Name" value={userSettings.firstName}
                  onChange={handleUserSettingsChange('firstName')}
                  variant="outlined" margin="normal" disabled={!isEditing}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Last Name" value={userSettings.lastName}
                  onChange={handleUserSettingsChange('lastName')}
                  variant="outlined" margin="normal" disabled={!isEditing}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Email Address" value={userSettings.email}
                  onChange={handleUserSettingsChange('email')}
                  variant="outlined" margin="normal" disabled={!isEditing}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Phone Number" value={userSettings.phone}
                  onChange={handleUserSettingsChange('phone')}
                  variant="outlined" margin="normal" disabled={!isEditing}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" disabled={!isEditing} sx={fieldSx}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={userSettings.role}
                    onChange={handleUserSettingsChange('role')}
                    label="Role"
                    readOnly={!isEditing}
                  >
                    <MenuItem value="Administrator">Administrator</MenuItem>
                    <MenuItem value="Property Manager">Property Manager</MenuItem>
                    <MenuItem value="Accountant">Accountant</MenuItem>
                    <MenuItem value="Maintenance Staff">Maintenance Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {isEditing && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2, borderColor: palette.border }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ borderColor: palette.border, color: palette.text.secondary }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  sx={{ bgcolor: Colors.primary.main, '&:hover': { bgcolor: Colors.primary.dark } }}
                >
                  Save Changes
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProfileSettings;